import { useState, useCallback } from "react";
import type { FilterState, SavedFilter } from "@/hooks/useFilterPersistence";

interface SavedFiltersProps {
  currentFilters: FilterState;
  presets: SavedFilter[];
  matchingPreset: SavedFilter | null;
  isFilterEmpty: (filters: FilterState) => boolean;
  getFilterLabel: (filters: FilterState) => string;
  onSavePreset: (name: string, filters: FilterState) => SavedFilter;
  onDeletePreset: (id: string) => void;
  onApplyPreset: (filters: FilterState) => void;
}

export default function SavedFilters({
  currentFilters,
  presets,
  matchingPreset,
  isFilterEmpty,
  getFilterLabel,
  onSavePreset,
  onDeletePreset,
  onApplyPreset,
}: SavedFiltersProps) {
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleSave = useCallback(() => {
    onSavePreset(saveName, currentFilters);
    setSaveName("");
    setShowSaveInput(false);
  }, [saveName, currentFilters, onSavePreset]);

  const handleSaveKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && saveName.trim()) {
        handleSave();
      }
      if (e.key === "Escape") {
        setShowSaveInput(false);
        setSaveName("");
      }
    },
    [saveName, handleSave],
  );

  const handleDeleteConfirm = useCallback(
    (id: string) => {
      onDeletePreset(id);
      setConfirmDeleteId(null);
    },
    [onDeletePreset],
  );

  const activeFilterLabel = getFilterLabel(currentFilters);
  const hasActiveFilters = !isFilterEmpty(currentFilters);
  const isAlreadySaved = matchingPreset !== null;

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-2">
        {/* Save button / Saved indicator */}
        {hasActiveFilters && (
          <>
            {isAlreadySaved ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200/70 rounded-full text-xs font-medium text-green-700">
                <i className="ri-bookmark-fill text-sm"></i>
                Đã lưu: {matchingPreset.name}
                <button
                  onClick={() => {
                    if (matchingPreset) onDeletePreset(matchingPreset.id);
                  }}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-200 transition-colors cursor-pointer ml-0.5"
                  aria-label={`Xóa bộ lọc đã lưu ${matchingPreset.name}`}
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            ) : showSaveInput ? (
              <div className="inline-flex items-center gap-1.5 bg-background-100 border border-background-200/70 rounded-full px-3 py-1">
                <i className="ri-bookmark-line text-sm text-foreground-500"></i>
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={handleSaveKeyDown}
                  placeholder="Tên bộ lọc..."
                  className="text-xs bg-transparent border-none outline-none text-foreground-800 placeholder:text-foreground-400 w-28"
                  autoFocus
                />
                <button
                  onClick={handleSave}
                  disabled={!saveName.trim()}
                  className="flex items-center justify-center w-5 h-5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Xác nhận lưu"
                >
                  <i className="ri-check-line text-xs"></i>
                </button>
                <button
                  onClick={() => {
                    setShowSaveInput(false);
                    setSaveName("");
                  }}
                  className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label="Hủy"
                >
                  <i className="ri-close-line text-xs text-foreground-500"></i>
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setSaveName(activeFilterLabel);
                  setShowSaveInput(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-full text-xs font-medium text-foreground-600 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-line text-sm"></i>
                Lưu bộ lọc này
              </button>
            )}
          </>
        )}

        {/* Saved presets */}
        {presets.map((preset) => {
          const isActive =
            matchingPreset?.id === preset.id;
          const isConfirming = confirmDeleteId === preset.id;

          if (isConfirming) {
            return (
              <span
                key={preset.id}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200/70 rounded-full text-xs"
              >
                <span className="text-red-600 font-medium">Xóa "{preset.name}"?</span>
                <button
                  onClick={() => handleDeleteConfirm(preset.id)}
                  className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer"
                  aria-label="Xác nhận xóa"
                >
                  <i className="ri-check-line text-xs"></i>
                </button>
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-red-200 transition-colors cursor-pointer"
                  aria-label="Hủy xóa"
                >
                  <i className="ri-close-line text-xs text-red-500"></i>
                </button>
              </span>
            );
          }

          return (
            <button
              key={preset.id}
              onClick={() => onApplyPreset(preset.filters)}
              onContextMenu={(e) => {
                e.preventDefault();
                setConfirmDeleteId(preset.id);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? "bg-accent-100 text-accent-700 border border-accent-300"
                  : "bg-background-100 text-foreground-600 border border-background-200/70 hover:border-accent-300 hover:text-accent-700"
              }`}
              title={`${preset.name}\n${getFilterLabel(preset.filters)}\nClick phải để xóa`}
            >
              <i className={`text-sm ${isActive ? "ri-bookmark-fill" : "ri-bookmark-line"}`}></i>
              {preset.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}